import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { searchRoutes, loadRoutes, updateFilter } from './RouteSelectionSlice';
import { clearChartSettings } from '../chartSettings/ChartSettingsSlice';
import { clearChartData } from '../charts/ChartsSlice';
import analyticsService from '../../services/analytics';

export function useRouteSelectionLogic() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const serverPage = useSelector(state => state.routeSelection.current_server_page);
    const authToken = useSelector(state => state.auth.connections.rwgps?.access_token);
    const stravaAuth = useSelector(state => state.auth.connections.strava);
    const connectionsLoaded = useSelector(state => state.auth.connectionsLoaded);
    const allRoutes = useSelector(state => state.routeSelection.allRoutes);
    const initialLoadComplete = useRef(false);

    const [searchTerm, setSearchTerm] = useState('');
    const [routeIdError, setRouteIdError] = useState('');
    const [loadingRouteId, setLoadingRouteId] = useState(false);
    const [routeIdDialogOpen, setRouteIdDialogOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [hasRouteIdInput, setHasRouteIdInput] = useState(false);

    const routeIdInputRef = useRef(null);
    const open = Boolean(anchorEl);

    useEffect(() => {
        // Only load routes if we have an auth token or are offline
        const shouldLoadRoutes = authToken || stravaAuth || !navigator.onLine;

        if (shouldLoadRoutes) {
            // If routes are already loaded in Redux, don't reload from server
            // Only fetch from server on initial page load (when routes don't exist)
            if (allRoutes && allRoutes.length > 0) {
                initialLoadComplete.current = true;
            } else if (!initialLoadComplete.current) {
                // Only dispatch loadRoutes if routes don't exist (initial page load)
                dispatch(loadRoutes({ pageNum: serverPage }));
                initialLoadComplete.current = true;
            } else if (serverPage > 1) {
                // Only dispatch for page changes after initial load
                dispatch(loadRoutes({ pageNum: serverPage }));
            }
        }
        // clear any selected route and chart settings
        dispatch(clearChartData());
        dispatch(clearChartSettings({}));
    }, [dispatch, serverPage, authToken, stravaAuth, allRoutes]);

    const handleSearch = (value) => {
        setSearchTerm(value);
        if (value === '') {
            // Only load routes if we have an auth token or are offline
            if (authToken || stravaAuth || !navigator.onLine) {
                dispatch(loadRoutes({ pageNum: 1 }));
            }
        } else {
            dispatch(searchRoutes(value));
        }
    };

    const handleFilterClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleFilterClose = () => {
        setAnchorEl(null);
    };

    const handleFilterChange = (filterType, value) => {
        dispatch(updateFilter({ filterType, value }));
    };

    const handleLoadRouteById = async () => {
        const routeId = routeIdInputRef.current?.value?.trim() || '';
        if (!routeId) {
            setRouteIdError('Please enter a route ID');
            return;
        }

        const routeIdNum = parseInt(routeId);
        if (isNaN(routeIdNum)) {
            setRouteIdError('Route ID must be a number');
            return;
        }

        setLoadingRouteId(true);
        setRouteIdError('');

        try {
            // Check if we're online
            if (!navigator.onLine) {
                setRouteIdError('Cannot load route while offline');
                setLoadingRouteId(false);
                return;
            }

            // Check if we have an auth token
            if (!authToken) {
                setRouteIdError('No authentication token found');
                setLoadingRouteId(false);
                return;
            }

            // Validate route exists by making API call
            const response = await fetch(`https://ridewithgps.com/routes/${routeIdNum}.json`, {
                headers: {
                    Accept: "application/json",
                    "x-rwgps-api-version": 2,
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                },
            });

            if (response.status === 404) {
                setRouteIdError('Route not found');
                setLoadingRouteId(false);
                return;
            } else if (response.status === 403) {
                setRouteIdError('Route is private or access denied');
                setLoadingRouteId(false);
                return;
            } else if (response.status !== 200) {
                setRouteIdError(`Failed to load route (${response.status})`);
                setLoadingRouteId(false);
                return;
            }

            // Try to parse the response to ensure it's valid JSON
            let routeData;
            try {
                routeData = await response.json();
            } catch (parseError) {
                setRouteIdError('Invalid route id');
                setLoadingRouteId(false);
                return;
            }

            // Validate that we actually got route data
            if (!routeData || !routeData.route || !routeData.route.id) {
                setRouteIdError('Invalid route id');
                setLoadingRouteId(false);
                return;
            }

            // Route exists and is valid, navigate to cues page
            setRouteIdDialogOpen(false);
            if (routeIdInputRef.current) {
                routeIdInputRef.current.value = '';
            }
            setHasRouteIdInput(false);

            // Track route selection by ID
            analyticsService.trackRouteSelection(routeIdNum, routeData.route.name);

            // Route loaded from RWGPS, so source is rwgps
            navigate(`/cues/rwgps/${routeIdNum}`);
        } catch (error) {
            console.error('Error loading route:', error);
            setRouteIdError('Failed to load route');
        } finally {
            setLoadingRouteId(false);
        }
    };

    const handleRouteIdKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleLoadRouteById();
        }
    };

    const handleOpenRouteIdDialog = () => {
        setRouteIdDialogOpen(true);
        if (routeIdInputRef.current) {
            routeIdInputRef.current.value = '';
        }
        setRouteIdError('');
    };

    const handleCloseRouteIdDialog = () => {
        setRouteIdDialogOpen(false);
        if (routeIdInputRef.current) {
            routeIdInputRef.current.value = '';
        }
        setRouteIdError('');
    };

    const handleRouteIdInputChange = (e) => {
        setHasRouteIdInput(e.target.value.trim().length > 0);
    };

    return {
        // State
        searchTerm,
        routeIdError,
        loadingRouteId,
        routeIdDialogOpen,
        anchorEl,
        hasRouteIdInput,
        open,
        connectionsLoaded,
        routeIdInputRef,

        // Handlers
        handleSearch,
        handleFilterClick,
        handleFilterClose,
        handleFilterChange,
        handleLoadRouteById,
        handleRouteIdKeyDown,
        handleOpenRouteIdDialog,
        handleCloseRouteIdDialog,
        handleRouteIdInputChange,
    };
}
